import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import logo from "../assets/images/logo.svg";
import faqimage from "../assets/images/faq.svg";
import partnerbg from "../assets/images/partner-1.jpg";

import superior from "../assets/images/influencer.png";
import fastDelivery from "../assets/images/convenience.png";
import reliability from "../assets/images/mail.png";
import { AddSellerInq } from "../API/ContactAPI";
import { getAllFAQ } from "../API/FAQApi";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { PuffLoader } from "react-spinners";


// ✅ Features
const features = [
    {
        icon: superior,
        alt: "Attract-new-customers",
        title: "Attract new customers",
        description:
            "Join as a Rotiey Maker – earn from home by making fresh rotis and become self-reliant!",
    },
    {
        icon: fastDelivery,
        alt: "Doorstep-delivery-convenience",
        title: "Doorstep delivery convenience",
        description:
            "Join as a Delivery Partner – deliver happiness, earn on your schedule, and grow with us!",
    },
    {
        icon: reliability,
        alt: "Onboarding-support",
        title: "Onboarding support",
        description:
            "Get the Homemade Experience – enjoy fresh, healthy, and lovingly cooked meals just like home!",
    },
];

// ✅ Form Validation
const PartnerSchema = Yup.object().shape({
    Name: Yup.string().required("Name is required"),
    Email: Yup.string().email("Invalid email").required("Email is required"),
    Phone: Yup.string()
        .matches(/^[0-9]+$/, "Phone must be only digits")
        .required("Phone number is required"),
    Password: Yup.string().required("Password is required"),
    AddressLine1: Yup.string().required("Address is required"),
    LandMark: Yup.string().required("LandMark is required"),
    Area: Yup.string().required("Area is required"),
    Pincode: Yup.string().required("Pincode is required"),
    City: Yup.string().required("City is required"),
    State: Yup.string().required("State is required"),
    Country: Yup.string().required("Country is required"),
});


const Partner = () => {
    const [faqs, setFaqs] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const formRef = useRef(null);
    const faqRef = useRef(null);
    const [isFormLoading, setIsFormLoading] = useState(false);
    // ✅ Initial Values
    const initialValues = {
        Name: "",
        Email: "",
        CCODE: "",
        Phone: "",
        Password: "",
        AddressLine1: "",
        LandMark: "",
        Area: "",
        Pincode: "",
        City: "",
        State: "",
        Country: "",
        Lat: "0000",
        Lng: "0000",
    };

    // ✅ Submit Handler
    const handleSubmit = async (values, { resetForm }) => {
        setIsFormLoading(true);
        try {
            const result = await AddSellerInq(values);
            if (result.status === true) {
                resetForm();
            }
        } catch (err) {
            console.error("Submit error:", err);
        } finally {
            setIsFormLoading(false);
        }
    };

    const toggleFaq = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const scrollToForm = () => {
        formRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const scrollToFaq = () => {
        faqRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        const fetchFaqs = async () => {
            try {
                const res = await getAllFAQ();
                if (res) {
                    setFaqs(res.responseData);
                }
            } catch (error) {
                console.error("Error fetching FAQs:", error);
            }
        };
        fetchFaqs();
    }, []);

    return (
        <div className="bg-[#fff]">
            {isFormLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <PuffLoader color={"#83442B"} size={60} />
                </div>
            )}
            {/* Header */}
            <div
                style={{
                    backgroundImage: `url(${partnerbg})`,
                }}
                className="bg-cover bg-center md:pb-20 pt-8 pb-12 text-center text-white relative"
            >
                {/* Logo */}
                <div className="container mx-auto mb-10 px-5 md:px-3 flex justify-between">
                    <Link to={"/"}>
                        <img src={logo} alt="Rotiey Logo" className="h-8 md:h-10" />
                    </Link>
                    <button onClick={scrollToFaq} className="cursor-pointer">
                        Faqs
                    </button>
                </div>

                <h1 className="text-3xl md:text-5xl font-semibold mb-4 px-3">
                    Partner with Rotiey and <br />
                    grow your business
                </h1>
                <p className="px-3">
                    Lorem, ipsum dolor sit amet consectetur adipisicing elit. Doloremque,
                    mollitia.
                </p>
                <button
                    onClick={scrollToForm}
                    className="border mt-5 border-white text-white px-4 py-2 rounded-md hover:bg-theme2 font-medium"
                >
                    Register Now
                </button>
            </div>

            {/* 🔥 Steps + Documents Section */}
            <section className="container mx-auto px-4 py-12 grid md:grid-cols-2 gap-8">
                {/* Steps */}
                <div className="bg-gray-100 rounded-2xl p-6 shadow-sm">
                    <h3 className="text-base font-semibold text-gray-500">
                        In just 3 easy steps
                    </h3>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
                        Get Started with Us Today
                    </h2>

                    <div className="space-y-6">
                        {/* Step 1 */}
                        <div className="flex items-center gap-3">
                            <div className="flex flex-col items-center">
                                <span className="w-3 h-3 rounded-full bg-theme2"></span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Step 1</p>
                                <p className="font-semibold text-gray-700">
                                    Install the Rotiey Owner App
                                </p>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex items-center gap-3">
                            <div className="flex flex-col items-center">
                                <span className="w-3 h-3 rounded-full bg-theme2"></span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Step 2</p>
                                <p className="font-semibold text-gray-700">
                                    Login/Register using your phone number
                                </p>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex items-center gap-3">
                            <div className="flex flex-col items-center">
                                <span className="w-3 h-3 rounded-full bg-theme2"></span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Step 3</p>
                                <p className="font-semibold text-gray-700">Enter details</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Documents */}
                <div className="relative bg-gradient-to-b from-purple-50 to-white rounded-2xl p-6 border shadow-sm">
                    <div className="absolute top-0 right-0 w-10 h-10 bg-gradient-to-br from-purple-100 to-white rounded-bl-lg border-l border-b"></div>
                    <h3 className="font-semibold text-gray-700">
                        For an easy form filling process,
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                        you can keep these documents handy.
                    </p>
                    <hr className="mb-4" />
                    <ul className="space-y-2 font-medium text-gray-700">
                        <li>FSSAI License copy</li>
                        <li>Your Restaurant menu</li>
                        <li>Bank details</li>
                        <li>GSTIN</li>
                        <li>PAN card copy</li>
                    </ul>
                </div>
            </section>

            {/* ✅ Contact Form with Formik */}
            <div ref={formRef} className="container mx-auto px-4 py-10">
                <div className="loader-line"></div>
                <h2 className="text-xl md:text-3xl text-darktext font-semibold mb-1 mt-2">
                    Registration Form
                </h2>
                <p className="text-contentcolor mb-6">
                    Collaborate with us for mutual growth and success. Let's achieve more,
                    together.
                </p>
                <div className="bg-white border rounded-md shadow-sm p-6">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={PartnerSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue, handleReset }) => (
                            <Form className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Name Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">Name</label>
                                    <Field
                                        type="text"
                                        name="Name"
                                        placeholder="Enter your name"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="Name"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* Email Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">Email</label>
                                    <Field
                                        type="email"
                                        name="Email"
                                        placeholder="Enter your email"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="Email"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* Phone Number Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">Phone Number</label>
                                    <PhoneInput
                                        country={"in"}
                                        value={values.CCODE + values.Phone}
                                        onChange={(value, countryData) => {
                                            setFieldValue("CCODE", "+" + countryData.dialCode);
                                            setFieldValue(
                                                "Phone",
                                                value.replace(countryData.dialCode, "")
                                            );
                                        }}
                                        inputClass="!w-full !mt-1.5 !py-3 !border !rounded-md"
                                    />
                                    <ErrorMessage
                                        name="Phone"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* Password Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">Password</label>
                                    <Field
                                        type="password"
                                        name="Password"
                                        placeholder="Enter your password"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="Password"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* Address Line 1 Field */}
                                <div className="col-span-1 md:col-span-2">
                                    <label className="block text-darktext font-medium mb-1">Address Line 1</label>
                                    <Field
                                        type="text"
                                        name="AddressLine1"
                                        placeholder="Enter your address"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="AddressLine1"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* Landmark Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">Landmark</label>
                                    <Field
                                        type="text"
                                        name="LandMark"
                                        placeholder="Enter landmark"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="LandMark"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* Area Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">Area</label>
                                    <Field
                                        type="text"
                                        name="Area"
                                        placeholder="Enter area"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="Area"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* Pincode Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">Pincode</label>
                                    <Field
                                        type="text"
                                        name="Pincode"
                                        placeholder="Enter pincode"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="Pincode"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* City Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">City</label>
                                    <Field
                                        type="text"
                                        name="City"
                                        placeholder="Enter city"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="City"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* State Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">State</label>
                                    <Field
                                        type="text"
                                        name="State"
                                        placeholder="Enter state"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="State"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* Country Field */}
                                <div className="col-span-1">
                                    <label className="block text-darktext font-medium mb-1">Country</label>
                                    <Field
                                        type="text"
                                        name="Country"
                                        placeholder="Enter country"
                                        className="w-full border px-4 py-3 rounded-md focus:outline-none focus:border-gray-400"
                                    />
                                    <ErrorMessage
                                        name="Country"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                {/* Form Buttons */}
                                <div className="col-span-1 md:col-span-2 flex justify-end gap-4 mt-4">
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        className="px-6 py-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                                        disabled={isFormLoading}
                                    >
                                        CANCEL
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 rounded-full bg-theme2 hover:bg-black transition text-white"
                                        disabled={isFormLoading}
                                    >
                                        {isFormLoading ? "SUBMITTING..." : "SUBMIT"}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>

            {/* Why Partner Section */}
            <section className="bg-gray-100 py-12">
                <h2 className="md:text-4xl text-2xl font-semibold mb-6 text-center md:px-5 px-3">
                    Why should you partner with Rotiey?
                </h2>
                <div className="container mx-auto px-4 hidden md:block">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                        {features.map((feature, index) => (
                            <div key={index} className="flex">
                                <div className="flex flex-col items-center text-center p-6">
                                    <div className="border border-gray-300 bg-white rounded-lg flex items-center justify-center mb-3 p-2">
                                        <img
                                            className="w-12 object-contain"
                                            src={feature.icon}
                                            alt={feature.alt}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <h5 className="text-lg font-semibold text-gray-900">
                                            {feature.title}
                                        </h5>
                                        <h6 className="md:text-base text-sm text-gray-600">
                                            {feature.description}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="container mx-auto px-4 block md:hidden">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                        {features.map((feature, index) => (
                            <div key={index} className="flex border-b pb-3">
                                <div className="flex items-center w-full">
                                    <div className="border border-gray-300 bg-white rounded-lg flex items-center justify-center mr-4 p-2">
                                        <img
                                            className="w-16 object-contain"
                                            src={feature.icon}
                                            alt={feature.alt}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <h5 className="font-semibold text-gray-900">
                                            {feature.title}
                                        </h5>
                                        <h6 className="text-sm text-contentcolor">
                                            {feature.description}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <div ref={faqRef} className="py-12">
                <h2 className="md:text-4xl text-2xl font-semibold mb-6 text-center md:px-5 px-3">
                    Frequently Asked Questions
                </h2>
                <div className="container mx-auto px-4 grid md:grid-cols-3 md:gap-10 items-center">
                    <div className="flex my-3 md:my-0 justify-center md:col-span-1">
                        <img src={faqimage} alt="FAQ illustration" className="w-full md:w-80" />
                    </div>

                    <div className="my-3 md:my-0 md:col-span-2">
                        <div className="md:space-y-3 space-y-2">
                            {faqs.length > 0 ? (
                                faqs.map((faq, index) => (
                                    <div key={faq.Id} className="border rounded-md bg-white shadow-sm">
                                        <button
                                            onClick={() => toggleFaq(index)}
                                            className="flex justify-between items-center w-full px-4 text-lg py-4 font-medium text-left"
                                        >
                                            {faq.Title}
                                            {activeIndex === index ? (
                                                <FaChevronUp className="text-contentcolor" />
                                            ) : (
                                                <FaChevronDown className="text-contentcolor" />
                                            )}
                                        </button>
                                        {activeIndex === index && (
                                            <div className="px-4 pb-4 text-gray-600 text-base">
                                                {faq.Content}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-gray-500">No FAQs available.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Partner;
